import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, userRoles, homeFor } from "@/context/AuthContext";
import SiteGate from "@/components/SiteGate";
import Header from "@/components/Header";
import IdleTimeout from "@/components/IdleTimeout";
import SiteFooter from "@/components/SiteFooter";
import Home from "@/pages/Home";
import SearchPage from "@/pages/SearchPage";
import TripPage from "@/pages/TripPage";
import TicketPage from "@/pages/TicketPage";
import FindTicket from "@/pages/FindTicket";
import VerifyPage from "@/pages/VerifyPage";
import AuthPage from "@/pages/AuthPage";
import AccountPage from "@/pages/AccountPage";
import AdminPage from "@/pages/admin/AdminPage";
import StaffPortal from "@/pages/StaffPortal";
import TellerPage from "@/pages/TellerPage";
import CashierPage from "@/pages/CashierPage";
import ManagementPage from "@/pages/ManagementPage";
import TrackPage from "@/pages/TrackPage";
import ArrivalsBoard from "@/pages/ArrivalsBoard";
import ControlRoom from "@/pages/ControlRoom";
import LivePage from "@/pages/LivePage";
import HowItWorks from "@/pages/HowItWorks";
import Terms from "@/pages/Terms";
import Operations from "@/pages/Operations";
import AnalyticsTracker from "@/components/AnalyticsTracker";

const appParam = new URLSearchParams(window.location.search).get("app");
if (appParam) localStorage.setItem("gp_app", appParam);

function RequireRole({ roles, children }) {
  const { user } = useAuth();
  const location = useLocation();
  const loginPath = roles.includes("customer") ? "/login" : "/admin";
  if (user === null) return <div className="grid h-[60vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div>;
  if (!user) return <Navigate to={loginPath} replace state={{ next: location.pathname + location.search }} />;
  if (!userRoles(user).some((r) => roles.includes(r))) return <Navigate to="/" replace />;
  return children;
}

function AdminEntry() {
  const { user } = useAuth();
  if (user === null) return <div className="grid h-[60vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div>;
  if (user && userRoles(user).includes("admin")) return <AdminPage />;
  if (user && userRoles(user).some((r) => ["teller", "cashier", "station_master", "manager", "bus_owner"].includes(r))) return <Navigate to={homeFor(user)} replace />;
  return <StaffPortal />;
}

function App() {
  return (
    <HelmetProvider>
    <SiteGate>
    <AuthProvider>
      <BrowserRouter>
        <div className="flex min-h-screen flex-col">
        <Header />
        <IdleTimeout />
        <AnalyticsTracker />
        <main className="min-h-[calc(100vh-4rem)] flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/trip/:id" element={<TripPage />} />
            <Route path="/ticket/:code" element={<TicketPage />} />
            <Route path="/find" element={<FindTicket />} />
            <Route path="/verify/:ticket" element={<VerifyPage />} />
            <Route path="/track" element={<TrackPage />} />
            <Route path="/track/:code" element={<TrackPage />} />
            <Route path="/live" element={<LivePage />} />
            <Route path="/arrivals" element={<ArrivalsBoard />} />
            <Route path="/arrivals/:stationId" element={<ArrivalsBoard />} />
            <Route path="/control" element={<RequireRole roles={["admin", "station_master", "manager"]}><ControlRoom /></RequireRole>} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/account" element={<RequireRole roles={["customer"]}><AccountPage /></RequireRole>} />
            <Route path="/admin" element={<AdminEntry />} />
            <Route path="/teller" element={<RequireRole roles={["teller"]}><TellerPage /></RequireRole>} />
            <Route path="/cashier" element={<RequireRole roles={["cashier", "admin"]}><CashierPage /></RequireRole>} />
            <Route path="/station" element={<RequireRole roles={["station_master", "admin"]}><AdminPage defaultTab="trips" only={["routes", "trips", "swaps", "transfer", "cancellations", "control", "buses", "owners", "drivers", "settlements", "station_cash", "trip_reports", "transactions", "ledger", "management", "route_performance"]} /></RequireRole>} />
            <Route path="/management" element={<RequireRole roles={["admin", "station_master", "manager"]}><ManagementPage /></RequireRole>} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/operations" element={<RequireRole roles={["admin", "station_master", "manager", "teller", "cashier", "bus_owner"]}><Operations /></RequireRole>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <SiteFooter />
        </div>
        <Toaster position="top-center" richColors theme="light" />
      </BrowserRouter>
    </AuthProvider>
    </SiteGate>
    </HelmetProvider>
  );
}

export default App;
